import React, { useMemo } from 'react';
import { translate } from '@utils/i18n';
import { Table, TableSize } from '@ui/molecules/Table/Table';
import { Accordion } from '@ui/molecules/Accordion';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import { Tag } from '@ui/atoms/Tag';
import { Pagination } from '@ui/molecules/Table/components/Pagination';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import { getGridColumns } from './columns';
import { getIssueTypeDisplayKey } from '../../utils/getIssueTypeDisplayKey';
const NS = 'features.iim_review.issues_grid';
function getGroupKey(issue, groupBy) {
    var _a, _b, _c;
    switch (groupBy) {
        case 'issueType':
            return getIssueTypeDisplayKey(issue);
        case 'status':
            return issue.status;
        case 'inspectableType':
            return (_b = (_a = issue.inspectable) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : '';
        case 'impact': {
            const hi = (_c = issue.issueData) === null || _c === void 0 ? void 0 : _c.highImpact;
            if (hi === null || hi === undefined)
                return 'n_a';
            return hi === true ? 'high' : 'low';
        }
        default:
            return '';
    }
}
function getGroupLabel(issue, groupBy) {
    var _a;
    switch (groupBy) {
        case 'issueType': {
            const displayKey = getIssueTypeDisplayKey(issue);
            return translate(displayKey, {
                namespace: `${NS}.issue_type_labels`,
                defaultValue: issue.issueType
            });
        }
        case 'status':
            return translate(issue.status, {
                namespace: 'features.iim_review.issue_card.status',
                defaultValue: issue.status
            });
        case 'inspectableType':
            return issue.inspectable ? humanizeInspectableType(issue.inspectable.type) : 'N/A';
        case 'impact': {
            const hi = (_a = issue.issueData) === null || _a === void 0 ? void 0 : _a.highImpact;
            if (hi === null || hi === undefined)
                return translate('n_a', { namespace: `${NS}.impact` });
            return hi === true
                ? translate('high', { namespace: `${NS}.impact` })
                : translate('low', { namespace: `${NS}.impact` });
        }
        default:
            return 'Other';
    }
}
function groupIssuesByBoundary(issues, groupBy) {
    if (groupBy === 'none')
        return [{ data: issues }];
    const groups = [];
    let currentKey = null;
    let currentGroup = [];
    let currentLabel = '';
    issues.forEach((issue) => {
        const key = getGroupKey(issue, groupBy);
        if (key !== currentKey) {
            if (currentGroup.length > 0)
                groups.push({ title: currentLabel, data: currentGroup });
            currentKey = key;
            currentLabel = getGroupLabel(issue, groupBy);
            currentGroup = [issue];
        }
        else {
            currentGroup.push(issue);
        }
    });
    if (currentGroup.length > 0)
        groups.push({ title: currentLabel, data: currentGroup });
    return groups;
}
export default function IssuesGridTable({ issues, groupBy, sortBy, sortDirection, onSort, selectedItems, onSelectionChange, onSelectAll, onGroupSelectAll, onRowClick, activeItemId = null, onPageChange, serverCurrentPage, serverTotalPages, serverTotalItems, pageSize = 25 }) {
    const columns = useMemo(() => getGridColumns(), []);
    const isGrouped = groupBy !== 'none';
    const issueRows = issues;
    const tableData = useMemo(() => groupIssuesByBoundary(issueRows, groupBy), [issueRows, groupBy]);
    const handleSelectionChange = (itemId, checked) => {
        onSelectionChange(Number(itemId), checked);
    };
    const handleSelectAll = (selectAll) => {
        onSelectAll(selectAll);
    };
    const handleRowClick = ({ item }, event) => {
        const target = event.target;
        if (target.closest('.table-column--selector'))
            return;
        onRowClick(item);
    };
    if (!isGrouped) {
        return (React.createElement(Table, { data: issueRows, columns: columns, variant: "listing", size: TableSize.Full, hover: true, selectable: true, selectedItems: selectedItems, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, getItemId: (issue) => issue.id, sortable: true, sortableColumns: ['issueType', 'status', 'inspectable', 'impact'], onSort: onSort, sortBy: sortBy, sortDirection: sortDirection, onRowClick: handleRowClick, activeItemId: activeItemId, onPageChange: onPageChange, serverCurrentPage: serverCurrentPage, serverTotalPages: serverTotalPages, serverTotalItems: serverTotalItems, pageSize: pageSize }));
    }
    const allPageIssueIds = issueRows.map((issue) => issue.id);
    const selectedOnPageCount = allPageIssueIds.filter((id) => selectedItems.includes(id)).length;
    const allPageSelected = allPageIssueIds.length > 0 && selectedOnPageCount === allPageIssueIds.length;
    const somePageSelected = selectedOnPageCount > 0 && !allPageSelected;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "issues-grid__grouped-select-all" },
            React.createElement(Checkbox, { id: "issues-grid-select-all", checked: allPageSelected, indeterminate: somePageSelected, onChange: (checked) => onSelectAll(checked), label: translate('all', { namespace: NS, defaultValue: 'All' }) })),
        React.createElement("div", { className: "issues-grid__grouped" },
            tableData.map((group, groupIdx) => {
                const groupIssueIds = group.data.map((issue) => issue.id);
                const allGroupSelected = groupIssueIds.length > 0 && groupIssueIds.every((id) => selectedItems.includes(id));
                return (React.createElement(Accordion, { key: `group-${groupIdx}`, variant: "section", defaultExpanded: true, selectable: true, checkboxProps: {
                        id: `issues-group-select-${groupIdx}`,
                        checked: allGroupSelected,
                        onChange: (checked) => {
                            onGroupSelectAll === null || onGroupSelectAll === void 0 ? void 0 : onGroupSelectAll(groupIssueIds, checked);
                        }
                    }, title: React.createElement("span", { className: "issues-grid__group-title" },
                        group.title,
                        React.createElement(Tag, { label: `Count: ${group.data.length}`, variant: "gray", colorStyle: "pastry", readOnly: true })), className: "issues-grid__group-accordion" },
                    React.createElement(Table, { tableId: `issues-group-table-${groupIdx}`, data: group.data, columns: columns, variant: "detail", size: TableSize.Full, hover: true, selectable: true, selectedItems: selectedItems, onSelectionChange: handleSelectionChange, onSelectAll: (selectAll) => {
                            onGroupSelectAll === null || onGroupSelectAll === void 0 ? void 0 : onGroupSelectAll(groupIssueIds, selectAll);
                        }, getItemId: (issue) => issue.id, sortable: true, sortableColumns: ['issueType', 'status', 'inspectable', 'impact'], onSort: onSort, sortBy: sortBy, sortDirection: sortDirection, onRowClick: handleRowClick, activeItemId: activeItemId })));
            }),
            onPageChange && serverTotalPages && serverTotalPages > 1 && (React.createElement(Pagination, { currentPage: serverCurrentPage !== null && serverCurrentPage !== void 0 ? serverCurrentPage : 1, totalPages: serverTotalPages, totalItems: serverTotalItems !== null && serverTotalItems !== void 0 ? serverTotalItems : 0, pageSize: pageSize, onPageChange: onPageChange, className: "issues-grid__group-pagination" })))));
}
